import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { MotivoNaoVenda } from '../../models/motivos-nao-venda/motivo-nao-venda';
import { WhatsAppOfficialAtendimento } from '../../models/whatsapp-oficial/whatsapp-oficial-atendimento';
import { WhatsAppOfficialConfiguracao } from '../../models/whatsapp-oficial/whatsapp-oficial-configuracao';
import { WhatsAppOfficialClienteVinculo } from '../../models/whatsapp-oficial/whatsapp-oficial-cliente-vinculo';
import { WhatsAppOfficialEtapa } from '../../models/whatsapp-oficial/whatsapp-oficial-etapa';
import { WhatsAppOfficialForwardSeller } from '../../models/whatsapp-oficial/whatsapp-oficial-forward-seller';
import { WhatsAppOfficialKanban } from '../../models/whatsapp-oficial/whatsapp-oficial-kanban';
import { WhatsAppOfficialMensagem } from '../../models/whatsapp-oficial/whatsapp-oficial-mensagem';
import { WhatsAppOficialIaSugestao } from '../../models/whatsapp-oficial/whatsapp-oficial-ia-sugestao';
import { WhatsAppOfficialTemplate } from '../../models/whatsapp-oficial/whatsapp-oficial-template';
import { WhatsAppUazapiSellerConfig } from '../../models/whatsapp-oficial/whatsapp-uazapi-seller-config';
import { WhatsAppOfficialDashboard, WhatsAppOfficialDashboardFiltro } from '../../models/whatsapp-oficial/whatsapp-oficial-dashboard';
import { WhatsAppTemplateMensagem, WhatsAppTemplateSeller, WhatsAppTemplateArquivoUploadResult } from '../../models/whatsapp-oficial/whatsapp-template-mensagem';

@Injectable({
  providedIn: 'root'
})

export class WhatsAppOfficialService {
  private baseUrl: string;
  private headers = new HttpHeaders();
  public ultimoErroEnvioMensagem: string = "";

  constructor(
    private http: HttpClient,
    @Inject("BASE_URL") baseUrl: string,
    private toastr: ToastrService
  ) {
    this.baseUrl = baseUrl;
  }

  private getGuidClienteNapis(): string {
    return localStorage.getItem("guidclientenapis");
  }

  private getSellerId(): number {
    return parseInt(localStorage.getItem("id"), 10);
  }

  private getSilentHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      "X-Skip-Loader": "true"
    });
  }

  public getRealtimeUrl(): string {
    return this.baseUrl + "api/WhatsAppOfficialRealtime/Subscribe?guidclientenapis=" + this.getGuidClienteNapis();
  }

  public getDashboard(filtro: WhatsAppOfficialDashboardFiltro): Observable<WhatsAppOfficialDashboard> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialDashboard>(this.baseUrl + "api/WhatsAppOfficial/GetDashboard", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "filtro": filtro
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar o dashboard de WhatsApp.");

        return of(new WhatsAppOfficialDashboard());
      })
    );
  }

  public getUazapiInstanceStatus(seller_id: number): Observable<WhatsAppUazapiSellerConfig> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppUazapiSellerConfig>(this.baseUrl + "api/WhatsAppOfficial/GetUazapiInstanceStatus", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "seller_id": seller_id
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao consultar a instancia.";

        this.ultimoErroEnvioMensagem = mensagemErro;
        this.toastr.error("", mensagemErro);

        return of(new WhatsAppUazapiSellerConfig());
      })
    );
  }

  public atualizarQrCodeUazapi(seller_id: number): Observable<WhatsAppUazapiSellerConfig> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppUazapiSellerConfig>(this.baseUrl + "api/WhatsAppOfficial/AtualizarQrCodeUazapi", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "seller_id": seller_id
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao atualizar o QR Code da instancia.";

        this.ultimoErroEnvioMensagem = mensagemErro;
        this.toastr.error("", mensagemErro);

        return of(new WhatsAppUazapiSellerConfig());
      })
    );
  }

  public getConfiguration(): Observable<WhatsAppOfficialConfiguracao> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialConfiguracao>(this.baseUrl + "api/WhatsAppOfficial/GetConfiguration", {
      "guidclientenapis": this.getGuidClienteNapis()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar a configuração do WhatsApp Oficial.");

        return of(new WhatsAppOfficialConfiguracao());
      })
    );
  }

  public upsertConfiguration(configuration: WhatsAppOfficialConfiguracao): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    configuration.guidclientenapis = this.getGuidClienteNapis();

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/UpsertConfiguration", {
      "configuration": configuration
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao salvar a configuração do WhatsApp Oficial.");

        return of(false);
      })
    );
  }

  public getStages(): Observable<WhatsAppOfficialEtapa[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialEtapa[]>(this.baseUrl + "api/WhatsAppOfficial/GetStages", {
      "guidclientenapis": this.getGuidClienteNapis()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar as etapas do WhatsApp Oficial.");

        return of([]);
      })
    );
  }

  public addStage(stage: WhatsAppOfficialEtapa): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    stage.guidclientenapis = this.getGuidClienteNapis();

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/AddStage", {
      "stage": stage
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao adicionar a etapa.");

        return of(false);
      })
    );
  }

  public updateStage(stage: WhatsAppOfficialEtapa): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/UpdateStage", {
      "stage": stage
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao atualizar a etapa.");

        return of(false);
      })
    );
  }

  public deleteStage(id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/DeleteStage", {
      "id": id
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao excluir a etapa.");

        return of(false);
      })
    );
  }

  public reorderStages(stage_ids: number[]): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/ReorderStages", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "stage_ids": stage_ids
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao reordenar as etapas.");

        return of(false);
      })
    );
  }

  public getInbox(): Observable<WhatsAppOfficialAtendimento[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialAtendimento[]>(this.baseUrl + "api/WhatsAppOfficial/GetInbox", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar a inbox do WhatsApp Oficial.");

        return of([]);
      })
    );
  }

  public getKanban(): Observable<WhatsAppOfficialKanban> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialKanban>(this.baseUrl + "api/WhatsAppOfficial/GetKanban", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar o kanban do WhatsApp Oficial.");

        return of(new WhatsAppOfficialKanban());
      })
    );
  }

  public getConversationById(id: number, exibirLoader: boolean = true): Observable<WhatsAppOfficialAtendimento> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialAtendimento>(this.baseUrl + "api/WhatsAppOfficial/GetConversationById", {
      "id": id,
      "seller_id": this.getSellerId()
    }, { headers: exibirLoader ? this.headers : this.getSilentHeaders() }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar o atendimento.");

        return of(null);
      })
    );
  }

  public getAtendimentosByClient(client_id: number): Observable<WhatsAppOfficialAtendimento[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialAtendimento[]>(this.baseUrl + "api/WhatsAppOfficial/GetAtendimentosByClient", {
      "client_id": client_id
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar o histórico de WhatsApp.");

        return of([]);
      })
    );
  }

  public openOrCreateConversation(client_id: number, telefone_ddd: string, telefone: string, contato_nome: string): Observable<WhatsAppOfficialAtendimento> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialAtendimento>(this.baseUrl + "api/WhatsAppOfficial/OpenOrCreateConversation", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "seller_id": this.getSellerId(),
      "request": {
        "client_id": client_id,
        "telefone_ddd": telefone_ddd,
        "telefone": telefone,
        "contato_nome": contato_nome
      }
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao abrir o atendimento no WhatsApp Oficial.");

        return of(null);
      })
    );
  }

  public captureConversation(conversation_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/CaptureConversation", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao capturar o atendimento.");

        return of(false);
      })
    );
  }

  public moveConversationStage(conversation_id: number, stage_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/MoveConversationStage", {
      "conversation_id": conversation_id,
      "stage_id": stage_id,
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao mover o atendimento.");

        return of(false);
      })
    );
  }

  public reopenConversation(conversation_id: number, stage_id: number): Observable<WhatsAppOfficialAtendimento> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialAtendimento>(this.baseUrl + "api/WhatsAppOfficial/ReopenConversation", {
      "conversation_id": conversation_id,
      "stage_id": stage_id,
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao reabrir o atendimento.");

        return of(null);
      })
    );
  }

  public finishWonConversation(conversation_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/FinishWonConversation", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao concluir o atendimento como ganho.");

        return of(false);
      })
    );
  }

  public finishLostConversation(conversation_id: number, no_order_done_reason_id: number, complemento: string): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/FinishLostConversation", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId(),
      "no_order_done_reason_id": no_order_done_reason_id,
      "complemento": complemento
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao concluir o atendimento como perda.");

        return of(false);
      })
    );
  }

  public getMessages(conversation_id: number, take: number = 30, before_message_id: number = null): Observable<WhatsAppOfficialMensagem[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialMensagem[]>(this.baseUrl + "api/WhatsAppOfficial/GetMessages", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId(),
      "take": take,
      "before_message_id": before_message_id
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar as mensagens.");

        return of([]);
      })
    );
  }

  public markConversationAsRead(conversation_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/MarkConversationAsRead", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        return of(false);
      })
    );
  }

  public listarContatos(conversation_id: number): Observable<{name: string, phone: string}[]> {
    return this.http.post<{name: string, phone: string}[]>(this.baseUrl + "api/WhatsAppOfficial/ListContacts", {conversation_id, seller_id: this.getSellerId()}, {headers: this.getSilentHeaders()}).pipe(catchError(error => {
      this.toastr.error("", this.getErrorMessage(error) || "Não foi possível carregar os contatos."); return of(null);
    }));
  }

  public enviarContato(conversation_id: number, contact: {name: string, phone: string}): Observable<WhatsAppOfficialMensagem> {
    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/SendContact", {conversation_id, seller_id: this.getSellerId(), contact}, {headers: this.getSilentHeaders()}).pipe(catchError(error => {
      this.toastr.error("", this.getErrorMessage(error) || "Não foi possível enviar o contato."); return of(null);
    }));
  }
  public alterarMensagem(conversation_id: number, message_id: number, texto: string, deletar: boolean): Observable<WhatsAppOfficialMensagem> {
    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/AlterarMensagem", {
      conversation_id, message_id, texto, deletar, seller_id: this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(catchError((error: HttpErrorResponse) => {
      this.toastr.error("", this.getErrorMessage(error) || "Não foi possível alterar a mensagem.");
      return of(null);
    }));
  }
  public sendTextMessage(conversation_id: number, texto: string, reply_to_message_id: number = null): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");
    this.ultimoErroEnvioMensagem = "";

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/SendTextMessage", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId(),
      "texto": texto,
      "reply_to_message_id": reply_to_message_id
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao enviar a mensagem.";

        this.ultimoErroEnvioMensagem = mensagemErro;
        this.toastr.error("", mensagemErro);

        return of(false);
      })
    );
  }

  /* IA - Agente WhatsApp */

  public getIaSugestaoPendente(conversation_id: number): Observable<WhatsAppOficialIaSugestao> {
    return this.http.post<WhatsAppOficialIaSugestao>(this.baseUrl + "api/WhatsAppOfficial/GetIaSugestaoPendente", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        return of(null);
      })
    );
  }

  public gerarIaSugestao(conversation_id: number): Observable<WhatsAppOficialIaSugestao> {
    return this.http.post<WhatsAppOficialIaSugestao>(this.baseUrl + "api/WhatsAppOfficial/GerarIaSugestao", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao gerar a sugestão da IA.");

        return of(null);
      })
    );
  }

  public aprovarIaSugestao(interacao_id: number, texto_final: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/AprovarIaSugestao", {
      "interacao_id": interacao_id,
      "seller_id": this.getSellerId(),
      "texto_final": texto_final
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao aprovar a sugestão da IA.");

        return of(false);
      })
    );
  }

  public aprovarEtapaFluxoPedido(interacao_id: number): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/AprovarEtapaFluxoPedido", {
      "interacao_id": interacao_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao aprovar a etapa do pedido.");

        return of(false);
      })
    );
  }

  public recusarIaSugestao(interacao_id: number): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/RecusarIaSugestao", {
      "interacao_id": interacao_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao recusar a sugestão da IA.");

        return of(false);
      })
    );
  }

  public atualizarIaModoConversa(conversation_id: number, modo: string): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/AtualizarIaModoConversa", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId(),
      "modo": modo
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao atualizar o modo da IA.");

        return of(false);
      })
    );
  }

  public getMarketingTemplates(): Observable<WhatsAppOfficialTemplate[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialTemplate[]>(this.baseUrl + "api/WhatsAppOfficial/GetMarketingTemplates", {
      "guidclientenapis": this.getGuidClienteNapis()
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao carregar os templates Marketing.");

        return of([]);
      })
    );
  }

  public sendTemplateMessage(conversation_id: number, template_name: string, language_code: string, preview_text: string, variables: any[], components: any[] = []): Observable<WhatsAppOfficialMensagem> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/SendTemplateMessage", {
      "seller_id": this.getSellerId(),
      "request": {
        "conversation_id": conversation_id,
        "template_name": template_name,
        "language_code": language_code,
        "preview_text": preview_text,
        "variables": variables,
        "components": components
      }
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao enviar o template.");

        return of(null);
      })
    );
  }

  public sendDocumentMessage(conversation_id: number, arquivo: File): Observable<WhatsAppOfficialMensagem> {
    this.ultimoErroEnvioMensagem = "";

    let formData = new FormData();
    formData.append("conversation_id", conversation_id.toString());
    formData.append("seller_id", this.getSellerId().toString());
    formData.append("arquivo", arquivo, arquivo.name);

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/SendDocumentMessage", formData, {
      headers: new HttpHeaders({ "X-Skip-Loader": "true" })
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao enviar o documento.";

        this.ultimoErroEnvioMensagem = mensagemErro;
        this.toastr.error("", mensagemErro);

        return of(null);
      })
    );
  }

  public sendImageMessage(conversation_id: number, arquivo: File, caption: string = ""): Observable<WhatsAppOfficialMensagem> {
    this.ultimoErroEnvioMensagem = "";

    let formData = new FormData();
    formData.append("conversation_id", conversation_id.toString());
    formData.append("seller_id", this.getSellerId().toString());
    formData.append("arquivo", arquivo, arquivo.name);
    formData.append("caption", caption || "");

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/SendImageMessage", formData, {
      headers: new HttpHeaders({ "X-Skip-Loader": "true" })
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao enviar a imagem.";

        this.ultimoErroEnvioMensagem = mensagemErro;
        this.toastr.error("", mensagemErro);

        return of(null);
      })
    );
  }

  public sendAudioMessage(conversation_id: number, arquivo: File): Observable<WhatsAppOfficialMensagem> {
    this.ultimoErroEnvioMensagem = "";

    let formData = new FormData();
    formData.append("conversation_id", conversation_id.toString());
    formData.append("seller_id", this.getSellerId().toString());
    formData.append("arquivo", arquivo, arquivo.name);

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/SendAudioMessage", formData, {
      headers: new HttpHeaders({ "X-Skip-Loader": "true" })
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao enviar o áudio.";

        this.ultimoErroEnvioMensagem = mensagemErro;
        this.toastr.error("", mensagemErro);

        return of(null);
      })
    );
  }

  public downloadInboundDocumentMessage(message_id: number): Observable<WhatsAppOfficialMensagem> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/DownloadInboundDocumentMessage", {
      "message_id": message_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao baixar o documento.";

        this.toastr.error("", mensagemErro);

        return of(null);
      })
    );
  }

  public downloadInboundImageMessage(message_id: number): Observable<WhatsAppOfficialMensagem> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/DownloadInboundImageMessage", {
      "message_id": message_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao baixar a imagem.";

        this.toastr.error("", mensagemErro);

        return of(null);
      })
    );
  }

  public downloadInboundAudioMessage(message_id: number): Observable<WhatsAppOfficialMensagem> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/DownloadInboundAudioMessage", {
      "message_id": message_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao baixar o áudio.";

        this.toastr.error("", mensagemErro);

        return of(null);
      })
    );
  }

  public downloadInboundVideoMessage(message_id: number): Observable<WhatsAppOfficialMensagem> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialMensagem>(this.baseUrl + "api/WhatsAppOfficial/DownloadInboundVideoMessage", {
      "message_id": message_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        let mensagemErro = this.getErrorMessage(error) || "Houve um erro ao baixar o vídeo.";

        this.toastr.error("", mensagemErro);

        return of(null);
      })
    );
  }

  public getLossReasons(): Observable<MotivoNaoVenda[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<MotivoNaoVenda[]>(this.baseUrl + "api/WhatsAppOfficial/GetLossReasons", {
      "guidclientenapis": this.getGuidClienteNapis()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar os motivos de perda.");

        return of([]);
      })
    );
  }

  public searchClientsForManualLink(filtro: string): Observable<WhatsAppOfficialClienteVinculo[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialClienteVinculo[]>(this.baseUrl + "api/WhatsAppOfficial/SearchClientsForManualLink", {
      "guidclientenapis": this.getGuidClienteNapis(),
      "filtro": filtro
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao buscar clientes.");

        return of([]);
      })
    );
  }

  public linkConversationClient(conversation_id: number, client_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/LinkConversationClient", {
      "conversation_id": conversation_id,
      "client_id": client_id,
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao vincular o cliente.");

        return of(false);
      })
    );
  }

  public unlinkConversationClient(conversation_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/UnlinkConversationClient", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao desvincular o cliente.");

        return of(false);
      })
    );
  }

  public linkConversationToSuggestedSeller(conversation_id: number, client_id: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/LinkConversationToSuggestedSeller", {
      "conversation_id": conversation_id,
      "client_id": client_id,
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao vincular o vendedor interno.");

        return of(false);
      })
    );
  }

  /* Templates de Mensagem */

  public getSellersAptosTemplateMensagem(): Observable<WhatsAppTemplateSeller[]> {
    return this.http.post<WhatsAppTemplateSeller[]>(this.baseUrl + "api/WhatsAppOfficial/GetSellersAptosTemplateMensagem", {
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar os vendedores aptos.");
        return of([]);
      })
    );
  }

  public getTemplatesMensagemGestao(): Observable<WhatsAppTemplateMensagem[]> {
    return this.http.post<WhatsAppTemplateMensagem[]>(this.baseUrl + "api/WhatsAppOfficial/GetTemplatesMensagemGestao", {
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar os templates de mensagem.");
        return of([]);
      })
    );
  }

  public getTemplatesMensagemDisponiveis(): Observable<WhatsAppTemplateMensagem[]> {
    return this.http.post<WhatsAppTemplateMensagem[]>(this.baseUrl + "api/WhatsAppOfficial/GetTemplatesMensagemDisponiveis", {
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar os templates disponíveis.");
        return of([]);
      })
    );
  }

  public salvarTemplateMensagem(template: WhatsAppTemplateMensagem): Observable<number> {
    return this.http.post<number>(this.baseUrl + "api/WhatsAppOfficial/SalvarTemplateMensagem", {
      "template": template,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao salvar o template.");
        return of(0);
      })
    );
  }

  public alternarAtivoTemplateMensagem(template_id: number, ativo: boolean): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/AlternarAtivoTemplateMensagem", {
      "template_id": template_id,
      "ativo": ativo,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao atualizar o template.");
        return of(false);
      })
    );
  }

  public uploadArquivoTemplateMensagem(arquivo: File): Observable<WhatsAppTemplateArquivoUploadResult> {
    let formData = new FormData();
    formData.append("seller_id", this.getSellerId().toString());
    formData.append("arquivo", arquivo, arquivo.name);

    return this.http.post<WhatsAppTemplateArquivoUploadResult>(this.baseUrl + "api/WhatsAppOfficial/UploadArquivoTemplateMensagem", formData, {
      headers: new HttpHeaders({ "X-Skip-Loader": "true" })
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao enviar o arquivo.");
        return of(null);
      })
    );
  }

  public enviarTemplateMensagem(conversation_id: number, template_id: number): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/EnviarTemplateMensagem", {
      "conversation_id": conversation_id,
      "template_id": template_id,
      "seller_id": this.getSellerId()
    }, { headers: this.getSilentHeaders() }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao enviar o template.");
        return of(false);
      })
    );
  }

  public getForwardSellers(): Observable<WhatsAppOfficialForwardSeller[]> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<WhatsAppOfficialForwardSeller[]>(this.baseUrl + "api/WhatsAppOfficial/GetForwardSellers", {
      "seller_id": this.getSellerId()
    }, { headers: this.headers }).pipe(
      catchError(() => {
        this.toastr.error("", "Houve um erro ao carregar os vendedores para encaminhamento.");

        return of([]);
      })
    );
  }

  public forwardConversation(conversation_id: number, seller_id_destino: number): Observable<boolean> {
    this.headers.set("Content-Type", "application/json");

    return this.http.post<boolean>(this.baseUrl + "api/WhatsAppOfficial/ForwardConversation", {
      "conversation_id": conversation_id,
      "seller_id": this.getSellerId(),
      "seller_id_destino": seller_id_destino
    }, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastr.error("", this.getErrorMessage(error) || "Houve um erro ao encaminhar o atendimento.");

        return of(false);
      })
    );
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error == null || error.error == null) {
      return null;
    }

    if (typeof error.error == "string") {
      return error.error.replace(/uazapi/gi, 'API do Whats');
    }

    if (error.error.message != null && error.error.message != "") {
      return String(error.error.message).replace(/uazapi/gi, 'API do Whats');
    }

    return null;
  }
}
